import { useState } from 'react'
import { CalendarClock, CheckSquare, Mail, MessageCircle, Phone, StickyNote, Workflow, type LucideIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { crm2Api, useCrm2Write, type Activity } from './api'
import { fieldClass, fromDateInput, KIND_LABEL, PersonSelect, shortDay, todayPlus, usePeopleNames, when } from './shared'

const ICON: Record<Activity['kind'], LucideIcon> = { note: StickyNote, email: Mail, call: Phone, meeting: CalendarClock, whatsapp: MessageCircle, task: CheckSquare, stage: Workflow }
export type LogKind = Exclude<Activity['kind'], 'stage'>
const KINDS: LogKind[] = ['note', 'call', 'meeting', 'email', 'whatsapp', 'task']

/** Everything that happened with the account, newest first, and a box to log the next thing. */
export function Timeline({
  accountId,
  dealId,
  items,
  contacts = [],
  kind: pickedKind,
  onKind,
  title = 'Timeline',
  defaultContactId = '',
}: {
  accountId: string
  dealId?: string
  items: Activity[]
  /** The people a conversation can be logged with, so it shows on their contact page. */
  contacts?: Array<{ id: string; name: string }>
  /** Controlled kind, for quick-action buttons elsewhere on the page. */
  kind?: (typeof KINDS)[number]
  onKind?: (k: (typeof KINDS)[number]) => void
  title?: string
  /** Who a new entry is with unless changed: the person, on their own contact page. */
  defaultContactId?: string
}) {
  const name = usePeopleNames()
  const [ownKind, setOwnKind] = useState<(typeof KINDS)[number]>('note')
  const kind = pickedKind ?? ownKind
  const setKind = (k: (typeof KINDS)[number]) => (onKind ? onKind(k) : setOwnKind(k))
  const [contactId, setContactId] = useState<string>(defaultContactId)
  const contactName = new Map(contacts.map((c) => [c.id, c.name]))
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [direction, setDirection] = useState<'in' | 'out'>('out')
  const [dueAt, setDueAt] = useState(() => todayPlus(2))
  const [assigneeId, setAssigneeId] = useState<string | null>(null)
  const [show, setShow] = useState<'all' | 'tasks' | 'talk'>('all')
  const add = useCrm2Write(crm2Api.addActivity, (a) => `${KIND_LABEL[a.kind]} added`)
  const tick = useCrm2Write(({ id, done }: { id: string; done: boolean }) => crm2Api.patchActivity(id, { done }))

  const submit = () =>
    add.mutate(
      {
        accountId,
        dealId: dealId ?? null,
        contactId: contactId || null,
        kind,
        subject: subject.trim() || null,
        body: body.trim() || null,
        direction: kind === 'email' || kind === 'whatsapp' || kind === 'call' ? direction : null,
        dueAt: kind === 'task' ? fromDateInput(dueAt) : null,
        assigneeId: kind === 'task' ? assigneeId : null,
      },
      { onSuccess: () => { setSubject(''); setBody('') } },
    )

  const visible = items.filter((a) => show === 'all' || (show === 'tasks' ? a.kind === 'task' : a.kind !== 'task' && a.kind !== 'stage'))

  return (
    <section className="grid gap-3 rounded-xl border border-border bg-card p-4 shadow-sm">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold">{title}</h3>
        <div className="ml-auto inline-flex rounded-lg border border-border p-0.5 text-xs" role="group" aria-label="Show">
          {(['all', 'talk', 'tasks'] as const).map((v) => (
            <button key={v} type="button" aria-pressed={show === v} onClick={() => setShow(v)} className={cn('rounded-md px-2 py-0.5', show === v ? 'bg-accent font-medium' : 'text-muted-foreground')}>
              {v === 'all' ? 'Everything' : v === 'talk' ? 'Conversations' : 'Tasks'}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-2 rounded-lg border border-border-soft p-3">
        <div className="flex flex-wrap gap-1">
          {KINDS.map((k) => {
            const Icon = ICON[k]
            return (
              <button key={k} type="button" aria-pressed={kind === k} onClick={() => setKind(k)} className={cn('inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs', kind === k ? 'border-brand bg-accent font-medium' : 'border-border text-muted-foreground hover:bg-accent')}>
                <Icon className="size-3.5" />
                {KIND_LABEL[k]}
              </button>
            )
          })}
        </div>
        <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={kind === 'task' ? 'What needs doing' : kind === 'email' ? 'Subject, or paste the Gmail link' : 'Short summary'} aria-label="Summary" />
        {kind !== 'task' ? <Textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)} placeholder={kind === 'whatsapp' ? 'Paste the WhatsApp exchange' : 'Details'} aria-label="Details" /> : null}
        <div className="flex flex-wrap items-center gap-2">
          {contacts.length && kind !== 'task' ? (
            <select aria-label="With" value={contactId} onChange={(e) => setContactId(e.target.value)} className={cn(fieldClass, 'w-auto')}>
              <option value="">With: nobody in particular</option>
              {contacts.map((c) => (
                <option key={c.id} value={c.id}>
                  With {c.name}
                </option>
              ))}
            </select>
          ) : null}
          {kind === 'email' || kind === 'whatsapp' || kind === 'call' ? (
            <select aria-label="Direction" value={direction} onChange={(e) => setDirection(e.target.value as 'in' | 'out')} className={cn(fieldClass, 'w-auto')}>
              <option value="out">We reached out</option>
              <option value="in">They reached us</option>
            </select>
          ) : null}
          {kind === 'task' ? (
            <>
              <Input type="date" value={dueAt} onChange={(e) => setDueAt(e.target.value)} className="w-40" aria-label="Due" />
              <div className="w-48">
                <PersonSelect label="Assignee" value={assigneeId} onChange={setAssigneeId} />
              </div>
            </>
          ) : null}
          <Button size="sm" className="ml-auto" onClick={submit} disabled={add.isPending || (kind === 'task' ? !subject.trim() : !subject.trim() && !body.trim())}>
            {kind === 'task' ? 'Add task' : `Log ${(KIND_LABEL[kind] ?? kind).toLowerCase()}`}
          </Button>
        </div>
      </div>

      <ol className="grid gap-2">
        {visible.map((a) => {
          const Icon = ICON[a.kind]
          return (
            <li key={a.id} className="flex gap-2.5 text-sm">
              <Icon className={cn('mt-0.5 size-4 shrink-0', a.kind === 'stage' ? 'text-text-muted' : 'text-brand')} />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-x-2">
                  {a.kind === 'task' ? (
                    <label className="inline-flex items-center gap-1.5">
                      <input type="checkbox" checked={Boolean(a.doneAt)} onChange={(e) => tick.mutate({ id: a.id, done: e.target.checked })} />
                      <span className={cn('font-medium', a.doneAt && 'text-muted-foreground line-through')}>{a.subject}</span>
                    </label>
                  ) : (
                    <span className={cn(a.kind === 'stage' ? 'text-muted-foreground' : 'font-medium')}>{a.subject ?? KIND_LABEL[a.kind]}</span>
                  )}
                  <span className="text-tiny text-text-muted">
                    {KIND_LABEL[a.kind]}
                    {a.contactId && contactName.get(a.contactId) ? ` · with ${contactName.get(a.contactId)}` : ''}
                    {a.direction ? (a.direction === 'in' ? ' · from them' : ' · from us') : ''} · {a.kind === 'task' ? `due ${shortDay(a.dueAt)} · ${name(a.assigneeId)}` : `${when(a.occurredAt)} · ${name(a.authorId)}`}
                  </span>
                </div>
                {a.body ? <p className="mt-0.5 whitespace-pre-wrap text-muted-foreground">{a.body}</p> : null}
              </div>
            </li>
          )
        })}
        {!visible.length ? <li className="text-sm text-muted-foreground">Nothing logged yet.</li> : null}
      </ol>
    </section>
  )
}
